import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, Pause, Plus, Check, Loader2 } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { useCart } from "../context/CartContext";
import { trackCoverUrl, eur } from "../lib/api";
import { toast } from "sonner";

export const TrackCard = ({ track, index = 0 }) => {
  const { current, playing, loading, playTrack } = usePlayer();
  const { add, items } = useCart();
  const isActive = current?.track_id === track.track_id;
  const inCart = items.some((i) => i.track_id === track.track_id);

  return (
    <motion.article
      data-testid={`track-card-${track.track_id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group border border-white/[0.08] bg-surface"
    >
      <div className="relative aspect-square overflow-hidden">
        <img
          src={trackCoverUrl(track)}
          alt={track.title}
          loading="lazy"
          className="h-full w-full object-cover transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:brightness-110"
        />
        <button
          onClick={() => playTrack(track)}
          data-testid={`play-track-${track.track_id}`}
          aria-label={`Écouter ${track.title}`}
          className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100"
        >
          <span className={`flex h-14 w-14 items-center justify-center rounded-full bg-amber-500 text-black transition-transform duration-300 hover:scale-110 ${isActive && playing ? "pulse-amber" : ""}`}>
            {isActive && loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : isActive && playing ? (
              <Pause className="h-5 w-5" />
            ) : (
              <Play className="ml-0.5 h-5 w-5" />
            )}
          </span>
        </button>
        <span className="absolute left-3 top-3 bg-black/75 px-2.5 py-1 text-xs font-bold tracking-[0.15em] text-amber-400">
          {eur(track.price)}
        </span>
        {track.avg_rating && (
          <span className="absolute right-3 top-3 flex items-center gap-1 bg-black/75 px-2 py-1 text-[11px] font-bold text-amber-400" data-testid={`rating-${track.track_id}`}>
            ★ {track.avg_rating} <span className="font-normal text-zinc-400">({track.review_count})</span>
          </span>
        )}
      </div>
      <div className="space-y-2.5 p-3">
        <Link
          to={`/morceau/${track.track_id}`}
          data-testid={`track-link-${track.track_id}`}
          className="block font-display text-base font-medium leading-tight transition-colors hover:text-amber-400"
        >
          {track.title}
        </Link>
        {track.artist_slug && (
          <Link
            to={`/artiste/${track.artist_slug}`}
            data-testid={`artist-link-${track.track_id}`}
            className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors hover:text-amber-400"
          >
            {track.artist}
          </Link>
        )}
        <div className="flex flex-wrap gap-1.5">
          {(track.usages || []).map((u) => (
            <span key={u} className="border border-amber-500/30 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-amber-400/90">
              {u}
            </span>
          ))}
          {(track.genres || []).slice(0, 2).map((g) => (
            <span key={g} className="border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              {g}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-white/[0.06] pt-3">
          <span className="text-[11px] uppercase tracking-[0.15em] text-zinc-500">
            {track.bpm ? `${track.bpm} BPM` : ""}
          </span>
          <button
            onClick={() => {
              if (inCart) return;
              add(track);
              toast.success(`« ${track.title} » ajouté au panier`);
            }}
            data-testid={`add-cart-${track.track_id}`}
            disabled={inCart}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] transition-colors ${
              inCart
                ? "cursor-default text-emerald-400"
                : "border border-white/15 text-zinc-300 hover:border-amber-500 hover:text-amber-400"
            }`}
          >
            {inCart ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {inCart ? "Au panier" : "Licence"}
          </button>
        </div>
      </div>
    </motion.article>
  );
};
